import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getPrompts,
  getPrompt,
  createPromptVersion,
  setCurrentPromptVersion,
  PromptListItem,
} from '../services/api'
import { useWorkflow } from '../contexts/WorkflowContext'

const DELIVERY_USE_CASES = ['waf', 'caf', 'aidw', 'security-assessment', 'maturity-assessment', 'azure', 'm365']
const PRE_SALES_USE_CASES = ['sow', 'proposal', 'wbs', 'discovery', 'pricing', 'managed-services']
const SYSTEM_CATEGORIES = ['system', 'ai-ify', 'user-prompt']

type PromptFilter = 'delivery' | 'pre_sales' | 'system'

export default function GeekModePromptEditor() {
  const queryClient = useQueryClient()
  const { isPreSales } = useWorkflow()
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(null)
  const [editedContent, setEditedContent] = useState('')
  const [changeNotes, setChangeNotes] = useState('')
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [promptFilter, setPromptFilter] = useState<PromptFilter>(isPreSales ? 'pre_sales' : 'delivery')

  // Fetch all prompts
  const { data: allPrompts = [], isLoading: loadingPrompts } = useQuery({
    queryKey: ['prompts'],
    queryFn: async () => {
      const response = await getPrompts({ include_inactive: true })
      return response.data
    },
  })

  // Filter prompts by workflow mode
  const prompts = useMemo(() => {
    return allPrompts.filter(p => {
      if (promptFilter === 'system') return SYSTEM_CATEGORIES.includes(p.category)
      if (promptFilter === 'pre_sales') return PRE_SALES_USE_CASES.includes(p.use_case)
      return DELIVERY_USE_CASES.includes(p.use_case)
    })
  }, [allPrompts, promptFilter])

  // Fetch selected prompt detail
  const { data: selectedPrompt, isLoading: loadingDetail } = useQuery({
    queryKey: ['prompt', selectedPromptId],
    queryFn: async () => {
      if (!selectedPromptId) return null
      const response = await getPrompt(selectedPromptId)
      return response.data
    },
    enabled: !!selectedPromptId,
  })

  // Create new version mutation
  const createVersionMutation = useMutation({
    mutationFn: async ({ promptId, content, notes }: { promptId: string; content: string; notes: string }) => {
      const response = await createPromptVersion(promptId, { content, change_notes: notes })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt', selectedPromptId] })
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
      setShowSaveModal(false)
      setChangeNotes('')
    },
  })

  // Set current version mutation
  const setCurrentMutation = useMutation({
    mutationFn: async ({ promptId, versionId }: { promptId: string; versionId: string }) => {
      await setCurrentPromptVersion(promptId, versionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt', selectedPromptId] })
      queryClient.invalidateQueries({ queryKey: ['prompts'] })
    },
  })

  // Group prompts by category
  const groupedPrompts = useMemo(() => {
    const groups: Record<string, PromptListItem[]> = {}
    const filtered = prompts.filter(p =>
      searchQuery === '' ||
      p.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    filtered.forEach(prompt => {
      const category = prompt.category || 'Other'
      if (!groups[category]) groups[category] = []
      groups[category].push(prompt)
    })
    return groups
  }, [prompts, searchQuery])

  const categoryOrder = [
    'strategy', 'plan', 'ready', 'migrate', 'innovate', 'govern', 'manage', 'summary', 'other'
  ]

  const sortedCategories = useMemo(() => {
    const categories = Object.keys(groupedPrompts)
    return categories.sort((a, b) => {
      const aIndex = categoryOrder.indexOf(a.toLowerCase())
      const bIndex = categoryOrder.indexOf(b.toLowerCase())
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b)
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })
  }, [groupedPrompts])

  // Handle prompt selection
  const handleSelectPrompt = (prompt: PromptListItem) => {
    setSelectedPromptId(prompt.id)
    setSelectedVersionId(null)
    setEditedContent('')
    setChangeNotes('')
  }

  // When prompt loads, set content from current version
  const displayedVersion = useMemo(() => {
    if (!selectedPrompt) return null
    if (selectedVersionId) {
      return selectedPrompt.versions?.find(v => v.id === selectedVersionId)
    }
    return selectedPrompt.current_version
  }, [selectedPrompt, selectedVersionId])

  // Update edited content when version changes
  useMemo(() => {
    if (displayedVersion) {
      setEditedContent(displayedVersion.content)
    }
  }, [displayedVersion?.id])

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  const hasChanges = displayedVersion && editedContent !== displayedVersion.content

  return (
    <div className="flex h-full">
      {/* Sidebar - Prompt List */}
      <div
        className="w-[200px] h-full flex flex-col"
        style={{ borderRight: '1px solid rgba(0, 255, 0, 0.2)' }}
      >
        {/* Search */}
        <div className="p-2" style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-2 py-1 font-mono text-xs rounded"
            style={{
              background: 'rgba(0, 255, 0, 0.05)',
              border: '1px solid rgba(0, 255, 0, 0.2)',
              color: '#00ff00',
              outline: 'none',
            }}
          />
        </div>

        {/* Workflow Filter */}
        <div className="flex px-1 py-1.5 gap-0.5" style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.1)' }}>
          {([
            { key: 'delivery' as PromptFilter, label: 'DLV', icon: 'fa-briefcase', color: '#3b82f6' },
            { key: 'pre_sales' as PromptFilter, label: 'SOW', icon: 'fa-handshake', color: '#10b981' },
            { key: 'system' as PromptFilter, label: 'SYS', icon: 'fa-cog', color: '#f59e0b' },
          ]).map(({ key, label, icon, color }) => (
            <button
              key={key}
              onClick={() => {
                setPromptFilter(key)
                setSelectedPromptId(null)
                setExpandedCategories(new Set())
              }}
              className="flex-1 px-1 py-1 font-mono text-[9px] uppercase tracking-wider rounded transition-all"
              style={{
                background: promptFilter === key ? `${color}33` : 'transparent',
                color: promptFilter === key ? color : 'rgba(0, 255, 0, 0.4)',
                border: `1px solid ${promptFilter === key ? `${color}66` : 'transparent'}`,
              }}
            >
              <i className={`fa-solid ${icon} mr-1`} />
              {label}
            </button>
          ))}
        </div>

        {/* Categories & Prompts */}
        <div className="flex-1 overflow-y-auto">
          {loadingPrompts ? (
            <div className="flex items-center justify-center h-20 font-mono text-xs" style={{ color: 'rgba(0, 255, 0, 0.5)' }}>
              <i className="fa-solid fa-spinner fa-spin mr-2" />
              Loading...
            </div>
          ) : (
            sortedCategories.map(category => (
              <div key={category}>
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-opacity-100"
                  style={{
                    background: 'rgba(0, 255, 0, 0.05)',
                    color: 'rgba(0, 255, 0, 0.7)',
                    borderBottom: '1px solid rgba(0, 255, 0, 0.1)',
                  }}
                >
                  <span>{category}</span>
                  <span className="flex items-center">
                    <span className="mr-2 text-[9px]" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
                      {groupedPrompts[category].length}
                    </span>
                    <i className={`fa-solid fa-chevron-${expandedCategories.has(category) ? 'down' : 'right'} text-[8px]`} />
                  </span>
                </button>
                {expandedCategories.has(category) && (
                  <div>
                    {groupedPrompts[category].map(prompt => (
                      <button
                        key={prompt.id}
                        onClick={() => handleSelectPrompt(prompt)}
                        className="w-full text-left px-3 py-1.5 font-mono text-[10px] transition-all"
                        style={{
                          background: selectedPromptId === prompt.id
                            ? 'rgba(0, 255, 0, 0.15)'
                            : 'transparent',
                          color: selectedPromptId === prompt.id ? '#00ff00' : 'rgba(0, 255, 0, 0.6)',
                          borderLeft: selectedPromptId === prompt.id
                            ? '2px solid #00ff00'
                            : '2px solid transparent',
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate">{prompt.display_name}</span>
                          <span
                            className="text-[8px] px-1 rounded"
                            style={{
                              background: 'rgba(0, 255, 0, 0.1)',
                              color: 'rgba(0, 255, 0, 0.5)',
                            }}
                          >
                            v{prompt.current_version_number || 1}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex flex-col">
        {!selectedPromptId ? (
          <div className="flex-1 flex flex-col items-center justify-center font-mono" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
            <i className="fa-solid fa-code text-4xl mb-4" />
            <div className="text-sm">Select a prompt to edit</div>
            <div className="text-[10px] mt-2">Expand a category on the left to browse prompts</div>
          </div>
        ) : loadingDetail ? (
          <div className="flex-1 flex items-center justify-center font-mono text-sm" style={{ color: 'rgba(0, 255, 0, 0.5)' }}>
            <i className="fa-solid fa-spinner fa-spin mr-2" />
            Loading prompt...
          </div>
        ) : selectedPrompt ? (
          <>
            {/* Header */}
            <div
              className="px-4 py-3"
              style={{ borderBottom: '1px solid rgba(0, 255, 0, 0.2)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-mono text-sm font-bold" style={{ color: '#00ff00' }}>
                    {selectedPrompt.display_name}
                  </div>
                  <div className="font-mono text-[10px]" style={{ color: 'rgba(0, 255, 0, 0.5)' }}>
                    {selectedPrompt.name} | {selectedPrompt.category} | {selectedPrompt.use_case}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Version selector */}
                  <select
                    value={selectedVersionId || selectedPrompt.current_version?.id || ''}
                    onChange={(e) => setSelectedVersionId(e.target.value || null)}
                    className="px-2 py-1 font-mono text-[10px] rounded"
                    style={{
                      background: 'rgba(0, 255, 0, 0.1)',
                      border: '1px solid rgba(0, 255, 0, 0.3)',
                      color: '#00ff00',
                      outline: 'none',
                    }}
                  >
                    {selectedPrompt.versions?.map(v => (
                      <option key={v.id} value={v.id}>
                        v{v.version_number} {v.is_current ? '(current)' : ''}
                      </option>
                    ))}
                  </select>

                  {/* Set as current button */}
                  {displayedVersion && !displayedVersion.is_current && (
                    <button
                      onClick={() => setCurrentMutation.mutate({
                        promptId: selectedPrompt.id,
                        versionId: displayedVersion.id,
                      })}
                      disabled={setCurrentMutation.isPending}
                      className="px-2 py-1 font-mono text-[10px] rounded transition-all"
                      style={{
                        background: 'rgba(0, 255, 255, 0.2)',
                        border: '1px solid rgba(0, 255, 255, 0.4)',
                        color: '#00ffff',
                      }}
                    >
                      {setCurrentMutation.isPending ? (
                        <i className="fa-solid fa-spinner fa-spin" />
                      ) : (
                        'Set Current'
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedPrompt.description && (
                <div className="font-mono text-[10px]" style={{ color: 'rgba(0, 255, 0, 0.6)' }}>
                  {selectedPrompt.description}
                </div>
              )}

              {/* Version info */}
              {displayedVersion && (
                <div className="flex items-center mt-2 font-mono text-[9px]" style={{ color: 'rgba(0, 255, 0, 0.4)' }}>
                  <span>Created: {new Date(displayedVersion.created_at).toLocaleString()}</span>
                  {displayedVersion.created_by && (
                    <span className="ml-3">by {displayedVersion.created_by}</span>
                  )}
                  {displayedVersion.change_notes && (
                    <span className="ml-3">| {displayedVersion.change_notes}</span>
                  )}
                </div>
              )}
            </div>

            {/* Editor */}
            <div className="flex-1 p-4 overflow-hidden">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-full p-3 font-mono text-xs rounded resize-none"
                style={{
                  background: 'rgba(0, 255, 0, 0.03)',
                  border: hasChanges
                    ? '2px solid rgba(255, 255, 0, 0.5)'
                    : '1px solid rgba(0, 255, 0, 0.2)',
                  color: 'rgba(0, 255, 0, 0.9)',
                  outline: 'none',
                }}
                placeholder="Enter prompt content..."
              />
            </div>

            {/* Footer with save button */}
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ borderTop: '1px solid rgba(0, 255, 0, 0.2)' }}
            >
              <div className="font-mono text-[10px]" style={{ color: hasChanges ? '#ffff00' : 'rgba(0, 255, 0, 0.4)' }}>
                {hasChanges ? 'Unsaved changes' : 'No changes'}
              </div>
              <button
                onClick={() => setShowSaveModal(true)}
                disabled={!hasChanges || createVersionMutation.isPending}
                className="px-4 py-1.5 font-mono text-xs rounded transition-all disabled:opacity-50"
                style={{
                  background: hasChanges ? 'rgba(0, 255, 0, 0.2)' : 'rgba(0, 255, 0, 0.05)',
                  border: `1px solid ${hasChanges ? 'rgba(0, 255, 0, 0.5)' : 'rgba(0, 255, 0, 0.2)'}`,
                  color: hasChanges ? '#00ff00' : 'rgba(0, 255, 0, 0.4)',
                }}
              >
                <i className="fa-solid fa-save mr-2" />
                Save New Version
              </button>
            </div>
          </>
        ) : null}
      </div>

      {/* Save Modal */}
      {showSaveModal && selectedPrompt && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ background: 'rgba(0, 0, 0, 0.8)' }}
          onClick={() => setShowSaveModal(false)}
        >
          <div
            className="w-[400px] p-4 rounded"
            style={{
              background: 'rgba(0, 20, 0, 0.98)',
              border: '1px solid rgba(0, 255, 0, 0.3)',
              boxShadow: '0 0 40px rgba(0, 255, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-mono text-sm mb-4" style={{ color: '#00ff00' }}>
              <i className="fa-solid fa-code-branch mr-2" />
              Save New Version
            </div>
            <div className="font-mono text-[10px] mb-3" style={{ color: 'rgba(0, 255, 0, 0.6)' }}>
              Creating version {(selectedPrompt.versions?.length || 0) + 1} for "{selectedPrompt.display_name}"
            </div>
            <textarea
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
              placeholder="What changed? (optional)"
              className="w-full h-20 p-2 font-mono text-xs rounded mb-4 resize-none"
              style={{
                background: 'rgba(0, 255, 0, 0.05)',
                border: '1px solid rgba(0, 255, 0, 0.2)',
                color: '#00ff00',
                outline: 'none',
              }}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-1.5 font-mono text-xs rounded"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(0, 255, 0, 0.3)',
                  color: 'rgba(0, 255, 0, 0.6)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  createVersionMutation.mutate({
                    promptId: selectedPrompt.id,
                    content: editedContent,
                    notes: changeNotes,
                  })
                }}
                disabled={createVersionMutation.isPending}
                className="px-4 py-1.5 font-mono text-xs rounded"
                style={{
                  background: 'rgba(0, 255, 0, 0.2)',
                  border: '1px solid rgba(0, 255, 0, 0.5)',
                  color: '#00ff00',
                }}
              >
                {createVersionMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check mr-2" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
